import cloneDeep from 'clone-deep'
import { fullStates } from 'config/states.constants'
import { RenderInput } from 'utils/RenderInput'

import { IMcrAdminView, IMcrConfig } from './types'

/**
 * Who the filer is and what the return covers.
 *
 * The NMLS ID and the state licence list are not preferences — they come off the company's NMLS
 * record, and the licence list decides how many RMLA sections the filing has. A state a licensee
 * holds a licence in and did no business in still files a zero RMLA, and nothing in the loan data
 * can work that out, which is why this list has to be entered rather than derived.
 */
export const McrFiler = ({
  view,
  config,
  onChange,
}: {
  view: IMcrAdminView
  config: IMcrConfig
  onChange: (next: IMcrConfig) => void
}) => {
  const set = (key: keyof IMcrConfig, value: any) => {
    const next = cloneDeep(config)
    ;(next as any)[key] = value
    onChange(next)
  }

  const tagList = (key: 'foreclosureTags' | 'bankruptcyTags') => (config[key] || []).join(', ')
  const setTagList = (key: 'foreclosureTags' | 'bankruptcyTags', value: string) =>
    set(
      key,
      String(value || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    )

  return (
    <div>
      {!!view.missing.length && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Still to complete before this return can be filed: {view.missing.join(', ')}.
        </div>
      )}

      <p className="text-md font-bold border-b mb-3 pb-1">Filer</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <RenderInput
          input={{
            inputType: 'text',
            title: 'Company NMLS ID',
            value: config.companyNmlsId,
            required: true,
          }}
          Key="companyNmlsId"
          onChange={(_k: string, v: any) => set('companyNmlsId', v)}
        />
        <RenderInput
          input={{
            inputType: 'text',
            title: 'Legal Name (as NMLS holds it)',
            value: config.legalName,
            required: true,
          }}
          Key="legalName"
          onChange={(_k: string, v: any) => set('legalName', v)}
        />
        <RenderInput
          input={{
            inputType: 'select',
            title: 'Filer Type',
            hasDefaultOption: false,
            options: { standard: 'Standard', expanded: 'Expanded' },
            value: config.filerType,
          }}
          Key="filerType"
          onChange={(_k: string, v: any) => set('filerType', v)}
        />
        <RenderInput
          input={{
            inputType: 'text',
            title: 'Fiscal Year End (MM-DD)',
            value: config.fiscalYearEnd,
            placeholder: '12-31',
          }}
          Key="fiscalYearEnd"
          onChange={(_k: string, v: any) => set('fiscalYearEnd', v)}
        />
      </div>
      <p className="-mt-4 mb-6 text-[11px] text-gray-400">
        A Standard filer submits the RMLA quarterly and the Financial Condition once a year; an Expanded
        filer submits both quarterly. The fiscal year end sets the window the FC income statement covers.
      </p>

      <p className="text-md font-bold border-b mb-3 pb-1">State Licences</p>
      <p className="mb-2 text-[12px] text-gray-500">
        One RMLA is filed per state licence. Leaving this empty falls back to every state the book
        touches, which is a useful preview but is not a filing — a licensed state with no activity still
        needs a zero RMLA, and only this list can say which those are.
      </p>
      <div className="mb-6">
        <RenderInput
          input={{
            inputType: 'multiselect',
            title: 'Licensed States',
            options: fullStates,
            value: config.states,
          }}
          Key="states"
          onChange={(_k: string, v: any) => set('states', Array.isArray(v) ? v : [])}
        />
      </div>

      <p className="text-md font-bold border-b mb-3 pb-1">Scope</p>
      <label className="mb-2 flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          checked={config.residentialOnly}
          onChange={(e) => set('residentialOnly', e.target.checked)}
        />
        Count only loans secured by residential (1-4 family) property
      </label>
      <p className="mb-6 text-[11px] text-gray-400">
        Most of this book is business-purpose lending against 1-4 family property, which the RMLA does
        cover. A genuinely commercial file is not residential mortgage activity and should not be on the
        form.
      </p>

      <p className="text-md font-bold border-b mb-3 pb-1">Foreclosure and Bankruptcy</p>
      <p className="mb-3 text-[12px] text-gray-500">
        The platform does not track these as loan fields, so the report reads them off tags or servicing
        statuses. They are reported separately on the printed report; in the NMLS upload those loans
        still count in the payment-status rows by how late they are, because the form has no bankruptcy
        line and reports foreclosure as its own rollforward.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <RenderInput
          input={{
            inputType: 'text',
            title: 'Foreclosure Tags (comma separated)',
            value: tagList('foreclosureTags'),
            placeholder: 'Foreclosure',
          }}
          Key="foreclosureTags"
          onChange={(_k: string, v: any) => setTagList('foreclosureTags', v)}
        />
        <RenderInput
          input={{
            inputType: 'text',
            title: 'Bankruptcy Tags (comma separated)',
            value: tagList('bankruptcyTags'),
            placeholder: 'Bankruptcy',
          }}
          Key="bankruptcyTags"
          onChange={(_k: string, v: any) => setTagList('bankruptcyTags', v)}
        />
        <RenderInput
          input={{
            inputType: 'multiselect',
            title: 'Foreclosure Servicing Statuses',
            options: view.servicingStatusLabels,
            value: config.foreclosureServicingStatuses,
          }}
          Key="foreclosureServicingStatuses"
          onChange={(_k: string, v: any) => set('foreclosureServicingStatuses', Array.isArray(v) ? v : [])}
        />
        <RenderInput
          input={{
            inputType: 'multiselect',
            title: 'Bankruptcy Servicing Statuses',
            options: view.servicingStatusLabels,
            value: config.bankruptcyServicingStatuses,
          }}
          Key="bankruptcyServicingStatuses"
          onChange={(_k: string, v: any) => set('bankruptcyServicingStatuses', Array.isArray(v) ? v : [])}
        />
      </div>
    </div>
  )
}
